import { useCallback, useEffect, useRef, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Upload, Trash2, Eye, Plus, Star, Image as ImageIcon, FileText, Loader2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Thumbnail autenticado: <img> não envia Authorization, então buscamos a imagem
 * via axios (blob) e geramos um object URL. Recarrega quando `version` muda.
 */
function ModelImageThumb({ modelId, side, hasImage, version }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoked = null;
    let alive = true;
    if (!hasImage) { setSrc(null); return; }
    api.get(`/admin/certificate-models/${modelId}/image/${side}`, { responseType: "blob" })
      .then((r) => {
        if (!alive) return;
        const url = URL.createObjectURL(r.data);
        revoked = url;
        setSrc(url);
      })
      .catch(() => { if (alive) setSrc(null); });
    return () => { alive = false; if (revoked) URL.revokeObjectURL(revoked); };
  }, [modelId, side, hasImage, version]);

  return (
    <div className="aspect-[1.414/1] bg-[hsl(var(--secondary))] rounded-lg overflow-hidden flex items-center justify-center border border-[hsl(var(--border))]">
      {src ? (
        <img src={src} alt={`${side}`} className="w-full h-full object-contain" />
      ) : (
        <div className="text-center p-3">
          <ImageIcon className="w-7 h-7 text-muted-foreground mx-auto mb-1" />
          <p className="text-xs text-muted-foreground">Sem imagem</p>
        </div>
      )}
    </div>
  );
}

function UploadSide({ model, side, label, uploading, onUpload, onDelete, version }) {
  const inputRef = useRef(null);
  const hasImage = side === "front" ? model.has_front_image : model.has_back_image;
  const sizeKb = side === "front" ? model.front_image_size_kb : model.back_image_size_kb;
  const busy = uploading === `${model.id}-${side}`;
  return (
    <div className="border-2 border-dashed border-[hsl(var(--border))] rounded-xl p-3" data-testid={`model-${model.id}-slot-${side}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-sm">{label}</h4>
        {hasImage ? <Badge variant="outline">{sizeKb} KB</Badge> : <Badge variant="secondary">vazio</Badge>}
      </div>
      <ModelImageThumb modelId={model.id} side={side} hasImage={hasImage} version={version} />
      <div className="flex gap-2 mt-3">
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          ref={inputRef}
          className="hidden"
          onChange={(e) => onUpload(model.id, side, e.target.files?.[0])}
        />
        <Button
          size="sm" variant="outline" className="flex-1"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid={`model-${model.id}-upload-${side}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
          {busy ? "Enviando…" : hasImage ? "Substituir" : "Enviar imagem"}
        </Button>
        {hasImage && (
          <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => onDelete(model.id, side)}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AdminModelosCertificado() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [version, setVersion] = useState(Date.now());
  const [previewing, setPreviewing] = useState(null);

  // Novo modelo
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newShowBack, setNewShowBack] = useState(true);
  const [creating, setCreating] = useState(false);

  // Exclusão
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/certificate-models")
      .then((r) => setModels(r.data || []))
      .catch(() => toast.error("Erro ao carregar modelos"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const createModel = async () => {
    if (!newName.trim()) { toast.error("Informe um nome"); return; }
    setCreating(true);
    try {
      await api.post("/admin/certificate-models", { name: newName.trim(), show_back: newShowBack });
      toast.success("Modelo criado! Agora envie as imagens de frente e verso.");
      setCreateOpen(false); setNewName(""); setNewShowBack(true);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar modelo");
    } finally { setCreating(false); }
  };

  const uploadImage = async (modelId, side, file) => {
    if (!file) return;
    setUploading(`${modelId}-${side}`);
    try {
      const fd = new FormData();
      fd.append("side", side);
      fd.append("file", file);
      await api.post(`/admin/certificate-models/${modelId}/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imagem (${side === "front" ? "frente" : "verso"}) enviada!`);
      setVersion(Date.now());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar imagem");
    } finally { setUploading(null); }
  };

  const deleteImage = async (modelId, side) => {
    try {
      await api.delete(`/admin/certificate-models/${modelId}/image/${side}`);
      toast.success("Imagem removida");
      setVersion(Date.now());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao remover imagem");
    }
  };

  const setDefault = async (model) => {
    try {
      await api.put(`/admin/certificate-models/${model.id}`, { is_default: !model.is_default });
      toast.success(!model.is_default ? "Definido como padrão" : "Removido dos padrões");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar");
    }
  };

  const previewPdf = async (model) => {
    setPreviewing(model.id);
    try {
      const r = await api.get(`/admin/certificate-models/${model.id}/preview`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar pré-visualização");
    } finally { setPreviewing(null); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/certificate-models/${deleteTarget.id}`);
      toast.success("Modelo excluído. Cursos que o usavam voltaram ao padrão do sistema.");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  return (
    <AdminLayout title="Modelos de Certificado">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Cadastre vários modelos de certificado (imagem de fundo de <strong>frente</strong> e <strong>verso</strong>).
          No cadastro do curso, escolha qual modelo usar. Cursos sem modelo escolhido usam o
          <strong> padrão do sistema</strong> (arte por área). O sistema escreve os dados do aluno por cima da imagem.
        </p>
        <Button onClick={() => setCreateOpen(true)} data-testid="model-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Novo modelo
        </Button>
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map((i) => <Card key={i} className="rounded-xl h-72 animate-pulse" />)}
        </div>
      ) : models.length === 0 ? (
        <Card className="rounded-xl">
          <CardContent className="py-16 text-center">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">Nenhum modelo cadastrado</p>
            <p className="text-sm text-muted-foreground mb-4">
              Crie um modelo e envie as imagens de frente e verso para vinculá-lo a um curso.
            </p>
            <Button onClick={() => setCreateOpen(true)} data-testid="model-empty-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Criar primeiro modelo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="models-grid">
          {models.map((m) => (
            <Card key={m.id} className="rounded-xl" data-testid={`model-card-${m.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    {m.name}
                    {m.is_default && (
                      <Badge className="bg-[hsl(var(--gold)/0.18)] text-[hsl(var(--gold-foreground))] border border-[hsl(var(--gold)/0.35)]">
                        <Star className="w-3 h-3 mr-1" /> Padrão
                      </Badge>
                    )}
                  </CardTitle>
                  <Badge variant={m.show_back ? "outline" : "secondary"}>
                    {m.show_back ? "Frente + Verso" : "Só frente"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <UploadSide model={m} side="front" label="Frente" uploading={uploading} onUpload={uploadImage} onDelete={deleteImage} version={version} />
                  <UploadSide model={m} side="back" label="Verso" uploading={uploading} onUpload={uploadImage} onDelete={deleteImage} version={version} />
                </div>
                <Separator />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={() => previewPdf(m)} disabled={previewing === m.id} data-testid={`model-preview-${m.id}`}>
                    {previewing === m.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
                    Pré-visualizar PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDefault(m)} data-testid={`model-default-${m.id}`}>
                    <Star className="w-3.5 h-3.5 mr-1" />
                    {m.is_default ? "Remover padrão" : "Definir padrão"}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-rose-600 ml-auto" onClick={() => setDeleteTarget(m)} data-testid={`model-delete-${m.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Excluir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog: novo modelo */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Novo modelo de certificado</DialogTitle>
            <DialogDescription>Dê um nome ao modelo. Depois envie as imagens de frente e verso.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do modelo</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex: Modelo Câmara SP 2026"
                data-testid="model-name-input"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-[hsl(var(--border))] p-3">
              <div>
                <Label className="cursor-pointer">Gerar verso (ementa + cargas horárias)</Label>
                <p className="text-xs text-muted-foreground">Desligue para certificado apenas frente.</p>
              </div>
              <Switch checked={newShowBack} onCheckedChange={setNewShowBack} data-testid="model-showback-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={createModel} disabled={creating} data-testid="model-create-confirm">
              {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Criar modelo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: excluir */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Excluir modelo</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o modelo <strong>{deleteTarget?.name}</strong>?
              Cursos que o utilizam voltarão a usar o modelo padrão do sistema.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={confirmDelete} data-testid="model-delete-confirm">
              <Trash2 className="w-4 h-4 mr-1" /> Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
